import {ChangeDetectionStrategy, Component, OnInit, signal} from '@angular/core';
import {PageTitleComponent} from "../../../components/page-title/page-title.component";
import {CustomFormComponent} from "../../../components/custom-form/custom-form.component";
import {TextInputComponent} from "../../../components/text-input/text-input.component";
import {FormInputsComponent} from "../../../components/form-inputs/form-inputs.component";
import {FormBuilder, Validators} from "@angular/forms";
import {getPasswordValidator} from "../../../validators";
import {AuthService} from "../../../services/auth/auth.service";
import {catchError, from, of, switchMap, tap, throwError} from "rxjs";
import {ActivatedRoute, Router} from "@angular/router";
import {MessageService} from "primeng/api";
import {Message} from "primeng/message";
import {SubmitButtonComponent} from "../../../components/submit-button/submit-button.component";
import {ButtonModule} from "primeng/button";
import {InputOtp} from "primeng/inputotp";
import {ReactiveFormsModule} from "@angular/forms";

type PageState =
  | { step: 'awaiting-code'; resent: boolean }
  | { step: 'entering-code'; resent: boolean }
  | { step: 'entering-password' }
  | { step: 'link-expired'; resendError?: string }
  | { step: 'code-mismatch'; resendError?: string };

@Component({
  selector: 'homey-reset-password-page',
  imports: [
    PageTitleComponent,
    CustomFormComponent,
    TextInputComponent,
    FormInputsComponent,
    Message,
    SubmitButtonComponent,
    ButtonModule,
    InputOtp,
    ReactiveFormsModule
  ],
  templateUrl: './reset-password-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResetPasswordPageComponent implements OnInit {
  constructor(
    private readonly fb: FormBuilder,
    private readonly authService: AuthService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly messageService: MessageService,
    private readonly router: Router,
  ) {
  }

  public ngOnInit(): void {
    this.email = this.activatedRoute.snapshot.queryParamMap.get('email');
    this.codeFromUrl = this.activatedRoute.snapshot.queryParamMap.get('code');
    const timestampParam = this.activatedRoute.snapshot.queryParamMap.get('timestamp');

    if (this.codeFromUrl) {
      this.form.controls.code.disable();
    }

    const linkExpired = timestampParam && this.isExpired(timestampParam);

    if (linkExpired) {
      this.state.set({step: 'link-expired'});
    } else if (this.codeFromUrl) {
      this.state.set({step: 'entering-password'});
    } else {
      this.state.set({step: 'awaiting-code', resent: false});
    }
  }

  public readonly form = this.fb.group({
    code: this.fb.nonNullable.control('', [Validators.required, Validators.minLength(6)]),
    password: this.fb.nonNullable.control('', [Validators.required, getPasswordValidator('relaxed')]),
  });

  protected email: string | null = null;
  protected codeFromUrl: string | null = null;
  protected readonly state = signal<PageState>({step: 'awaiting-code', resent: false});

  protected confirmCode(): void {
    if (this.form.controls.code.valid) {
      this.state.set({step: 'entering-password'});
    }
  }

  protected startEnteringCode(): void {
    const current = this.state();
    if (current.step === 'awaiting-code') {
      this.state.set({step: 'entering-code', resent: current.resent});
    }
  }

  protected readonly submit = (form: { code: string; password: string }) => {
    const code = this.codeFromUrl || form.code;
    const email = this.email;

    if (!code || !email) {
      throw new Error('Missing code or email');
    }

    return from(this.authService.homeyConfirmResetPassword(
      email,
      code,
      form.password,
    )).pipe(
      tap(() => this.messageService.add({
        severity: 'success',
        summary: 'Password reset successful!',
        detail: 'Your password has been reset successfully'
      })),
      switchMap(() => this.router.navigate(['..'], {relativeTo: this.activatedRoute})),
      catchError((error) => {
        if (error?.name === 'CodeMismatchException' || error?.name === 'ExpiredCodeException') {
          this.state.set({step: 'code-mismatch'});
          return of(undefined);
        }
        return throwError(() => error);
      })
    );
  }

  protected readonly resendResetLink = () => {
    if (!this.email) {
      this.state.update(s => ({...s, resendError: 'We could not find your email from this link. Please start the reset process again.'}) as PageState);
      return of(undefined);
    }

    return from(this.authService.homeyForgotPassword(this.email)).pipe(
      tap(() => {
        this.codeFromUrl = null;
        this.form.controls.code.enable();
        this.form.controls.code.reset();
        this.form.controls.password.reset();
        this.state.set({step: 'awaiting-code', resent: true});
      }),
      catchError((error) => {
        console.error('Error resending reset password email', error);
        this.state.update(s => ({...s, resendError: 'We could not send a new reset email. Please try again.'}) as PageState);
        return of(undefined);
      })
    );
  }

  private isExpired(timestampParam: string): boolean {
    const parsedTimestamp = Number(timestampParam);
    if (Number.isNaN(parsedTimestamp)) return false;
    return Date.now() - parsedTimestamp > 5 * 60 * 1000;
  }
}
